import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchRecycleOrderDetail, fetchSignedContractInfo } from 'apis/oms';
import { getMemberById } from 'apis/ums';
import authenticationApi from 'apis/authentication';
import {
  OmsRecycleOrderDetail,
  OmsRecycleOrderBaseInfo,
  OmsRecycleOrderCustomerInfo,
  OmsRecycleOrderUserProductInfo,
  OmsRecycleOrderProductInfo,
  OmsRecycleOrderLogisticsInfo,
  OmsRecycleOrderLogInfo,
  OmsRecycleOrderContractInfo,
  OmsRecycleOrderCollectionInfo,
} from 'types/oms';
import {
  OrderType,
  StatusMapping,
  ShopMaping,
  ProductImageSort,
  AccessoriesMapping,
  getRecycleOrderStatusText,
  createdFromList,
  OMS_RECYCLE_ORDER_STATUS_MAP,
} from 'constants/RecyclingConsignment';
import dayjs from 'dayjs';
import { thousands } from 'utils/tools';
import { UmsMember } from 'types/ums';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import { getCityListByCountryCode } from 'apis/home';
import useProductFormDataV2 from 'commons/hooks/useProductFormDataV2';
import GetPaymentInfo from 'utils/getPaymentInfo';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import { getLocalStorageLanguage } from '../../../commons';
import commonApi from 'apis/common';
import {
  getBagTypeNameFromCategoryId,
  isSupportedBagType as checkIsSupportedBagType,
} from '../recycling-consignment-order-v2/product-list/form-items/authentication-config';
import { SupplierType } from 'constants/france-tax';

const timeFormat = 'YYYY-MM-DD HH:mm';

const getTimeFormat = (date?: string): string => {
  if (!date) return '-';
  return dayjs(date).format(timeFormat);
};

const getSupplierTypeLabel = (
  supplierType?: 'COMPANY' | 'INDIVIDUAL' | null,
): string => {
  if (supplierType === SupplierType.INDIVIDUAL) {
    return i18n.t(LOCALS.supplier_type_individual);
  }
  if (supplierType === SupplierType.COMPANY) {
    return i18n.t(LOCALS.supplier_type_company);
  }
  return '-';
};

/** AI鉴定详情类型 */
export type AuthenticationDetailData = Awaited<
  ReturnType<typeof authenticationApi.getAuthenticationDetail>
>;

const useHandleDetailData = ({ id }: { id: string }) => {
  const [loading, setLoading] = useState(false);
  /** AI鉴定详情 */
  const [authenticationDetail, setAuthenticationDetail] =
    useState<AuthenticationDetailData | null>(null);
  /** 城市国家相关 */
  const { countryOptions, staffSelectOptions, productCategoryCascaderOptions } =
    useAppSelector(selectGlobalInfo);

  const { setProdInfo, LabelData } = useProductFormDataV2();
  const [fullStamp, setFullStamp] = useState('');

  const [cityList, setCityList] = useState<
    {
      code: string;
      countryCode: string;
      id: string;
      name: string;
    }[]
  >([]);
  /** 源数据 */
  const [dataSource, setDataSource] = useState<OmsRecycleOrderDetail>({});
  /** 客户数据 */
  const [memberData, setMemberData] = useState<UmsMember>();
  /** 当前步骤 */
  const [currentStep, setCurrentStep] = useState(0);
  /** 基础信息 */
  const [baseInfo, setBaseInfo] = useState<OmsRecycleOrderBaseInfo>({});
  /**客户信息 */
  const [customerInfo, setCustomerInfo] = useState<OmsRecycleOrderCustomerInfo>(
    {},
  );
  /** 用户提交商品信息 */
  const [userProductInfo, setUserProductInfo] =
    useState<OmsRecycleOrderUserProductInfo>({});
  /** 商品信息 */
  const [productInfo, setProductInfo] = useState<OmsRecycleOrderProductInfo>(
    {},
  );
  /** 物流信息 */
  const [logisticsInfo, setLogisticsInfo] =
    useState<OmsRecycleOrderLogisticsInfo>({});
  /** 退货信息 */
  const [returnGoodsInfo, setReturnGoods] =
    useState<OmsRecycleOrderLogisticsInfo>({});
  /** 收款信息 */
  const [collectionInfo, setCollectionInfo] =
    useState<OmsRecycleOrderCollectionInfo>({});
  /** 日志信息 */
  const [logList, setLogList] = useState<OmsRecycleOrderLogInfo[]>([]);
  /** 合同信息 */
  const [contractList, setContractList] = useState<
    OmsRecycleOrderContractInfo[]
  >([]);
  /** 上门签署合同（PDF 邮件） */
  const [signedContractInfo, setSignedContractInfo] = useState<{
    signedContractFileKey?: string | null;
    signedContractSentTime?: string | null;
    downloadUrl?: string | null;
  } | null>(null);

  useEffect(() => {
    if (
      Object.keys(dataSource).length > 0 &&
      memberData &&
      Object.keys(memberData).length > 0
    ) {
      const {
        omsRecycleOrder,
        omsRecycleOrderItem,
        omsRecycleOrderLogList,
        omsRecycleOrderLogistics,
        omsRecycleOrderProduct,
        umsMember,
        umsAdmin,
      } = dataSource;
      const emailMember = memberData?.email;
      if (omsRecycleOrder && omsRecycleOrderItem) {
        const {
          code,
          type,
          status = 0,
          createTime,
          memberId,
          storeId = 0,
          socialAccount,
          socialName,
          email,
          phone,
          currency,
          phaseType,
          financialPaymentVoucher = '',
          settlementType = 0,
          returnExpressCompany,
          returnTrackingNumber,
          returnVoucher = '',
          returnType = -1,
          createdFrom = 0,
          financialPaymentTime,
          orderId,
          orderSn,
          returnTime,
          completeTime,
          memberCredentialNo,
          contractExpirationTime,
          contractLinkShopAdmin,
          contractLinkShopMember,
          contractLinkWebsite,
          goodsReceivedTime,
          receiverName,
          orderCancelType,
          memberRemark,
          staffName,
          isTradeIn,
          supplierType,
        } = omsRecycleOrder || {};
        const {
          productAccessories,
          productPics,
          storeReceiptPics,
          productTitle,
          finalRecyclePrice,
          finalSalePrice,
          agreeFinalValuationTime,
          agreeFirstValuationTime,
          productId,
          firstValuationShopRemark,
          firstValuationStock = 0,
          firstValuationStockRemark,
          guestRemarks,
          productStatus = 0,
        } = omsRecycleOrderItem;
        const {
          shippingDocument = '[]',
          name,
          city,
          country,
          detailAddress,
          postCode,
          phone: LogisticsPhone,
          phone2,
          receiptPics,
        } = omsRecycleOrderLogistics || {};
        /** 步骤条处理 */
        if (status) {
          let stop = phaseType || 1;
          // 阶段类型 : 1-咨询，2-意向，3-合同 4-交易完成 5-取消订单 ,  下标从0开始
          if ([4, 5].includes(stop) || orderCancelType) {
            stop = 4;
          }
          setCurrentStep(stop - 1);
        }
        /** 基础信息 */
        const baseInfo = {
          submissionId: omsRecycleOrder?.submissionId,
          code,
          type: OrderType.find((d) => d.value === type)?.label,
          status: getRecycleOrderStatusText(omsRecycleOrder),
          store: ShopMaping[storeId],
          intentionTime: getTimeFormat(agreeFirstValuationTime),
          contractTime: getTimeFormat(agreeFinalValuationTime),
          tradingTime: getTimeFormat(completeTime),
          relatedSalesOrder: orderSn,
          orderId,
          associatedProductId: productId ? `${productId}` : '',
          channel:
            createdFromList.find((i) => i.value === createdFrom)?.label || '-',
          postStore: ShopMaping[storeId],
          productId,
          createTime: getTimeFormat(createTime),
          createId: '',
          createBy: '',
          createOwner: '',
          staffName: staffName || '-',
          isTradeIn:
            isTradeIn === 1
              ? i18n.t(LOCALS.yes)
              : isTradeIn === 0
                ? i18n.t(LOCALS.no)
                : '-',
          supplierType: getSupplierTypeLabel(supplierType),
        };
        // umsAdmin umsMember 都表示订单创建人
        // umsMember 表示订单用户
        if (umsAdmin) {
          baseInfo.createBy = umsAdmin.username;
          // baseInfo.createId = umsAdmin.id + ''; // 不让跳转
          baseInfo.createOwner = 'admin';
        } else {
          const { lastName = '', firstName = '', email = '' } = umsMember || {};
          baseInfo.createBy = lastName + firstName || email || '-';
          baseInfo.createId = umsMember?.id + '';
          baseInfo.createOwner = 'member';
        }
        setBaseInfo(baseInfo);
        /** 客户提交的信息 */
        const customerInfo = {
          name:
            dataSource.omsRecycleOrder?.name ||
            dataSource.omsRecycleOrderLogistics?.name,
          memberId,
          emailMember,
          socialAccount,
          socialName,
          social: `${socialName || ''} ${socialAccount || ''}`,
          email,
          phone: phone ? `+${phone.replace('+', '')}` : '-',
          code: memberCredentialNo,
          paymentDetails: memberData?.paymentDetails || '',
          guestRemarks,
        };
        setCustomerInfo(customerInfo);
        /** 用户提交商品信息 */
        let picList: string[] = [];
        if (productPics) {
          const picListParse = JSON.parse(productPics);
          // 处理图片顺序
          ProductImageSort.forEach((d) => {
            if (picListParse[d]) picList.push(picListParse[d]);
          });
        }
        const storeReceiptPicsList = storeReceiptPics
          ? JSON.parse(storeReceiptPics)
          : '';
        const accessoriesList = productAccessories
          ? JSON.parse(productAccessories).map(
              (d: string) => AccessoriesMapping[d],
            )
          : [];
        const userProductInfo = {
          rank: ['', i18n.t(LOCALS.unused), i18n.t(LOCALS.secondhand)][
            productStatus
          ],
          productAccessories,
          accessoriesList: accessoriesList.join('、'),
          productPics,
          picList,
          productTitle,
          memberRemark,
          storeReceiptPics,
          storeReceiptPicsList,
        };
        setUserProductInfo(userProductInfo);

        /** 实际商品信息 */
        if (LabelData) {
          const language = getLocalStorageLanguage();
          const { i18nList } = dataSource;
          const attrRankDesc =
            i18nList?.find((i) => i.lang === language)?.attrRankDesc ||
            omsRecycleOrderProduct?.attrRankDesc ||
            '';

          const color = LabelData.colorSelectLabelList?.join(',') || '';
          const rank = LabelData.rankSelectLabel;
          const material = LabelData.materialSelectLabelList?.join(',') || '';
          const hardware = LabelData.hardwareSelectLabel?.join(',') || '';
          const stamp = LabelData.stampSelectLabel;
          const info = [
            { label: i18n.t(LOCALS.color), value: color },
            {
              label: i18n.t(LOCALS.color_remark),
              value: omsRecycleOrderProduct?.attrColorRemark,
            },
            {
              label: i18n.t(LOCALS.rank),
              value: rank,
            },
            { label: i18n.t(LOCALS.rank_desc), value: attrRankDesc },
            { label: i18n.t(LOCALS.material), value: material },
            {
              label: i18n.t(LOCALS.material_remark),
              value: omsRecycleOrderProduct?.attrMaterialRemark,
            },
            { label: i18n.t(LOCALS.hardware), value: hardware },
            {
              label: i18n.t(LOCALS.hardware_remark),
              value: omsRecycleOrderProduct?.attrHardwareRemark,
            },
            {
              label: i18n.t(LOCALS.stamp),
              value: stamp,
            },
            {
              label: i18n.t(LOCALS.stamp_remark),
              value: omsRecycleOrderProduct?.attrStampRemark,
            },
            {
              label: i18n.t(LOCALS.ZPgEroRALL),
              value: omsRecycleOrderProduct?.fullStamp,
            },
          ];
          const category = LabelData.productCategorySelectLabelList;
          const accessory = LabelData.accessorySelectLabel?.join(',') || '';
          const attrSize = LabelData.productCategorySelectList?.size || '';
          const productInfo = {
            picList,
            category,
            accessoriesList: accessory,
            attrSize,
            info,
            firstValuationShopRemark,
            firstValuationStock: [
              '',
              i18n.t(LOCALS.unavailable),
              i18n.t(LOCALS.available),
            ][firstValuationStock],
            firstValuationStockRemark,
            fullStamp,
          };
          setProductInfo(productInfo);
        }
        /** 物流信息 */
        const imgList = JSON.parse(shippingDocument) || [];
        const logisticsInfo: OmsRecycleOrderLogisticsInfo = {
          name,
          phone: LogisticsPhone ? `+${LogisticsPhone}` : '-',
          phone2: phone2 ? `+${phone2}` : '-',
          postCode,
          country:
            countryOptions.find((d) => d.value === country)?.label || country,
          city: cityList.find((d) => d.code === city)?.name || city,
          detailAddress,
          mailingInfoPhoto:
            imgList.length === 2 ? [imgList[1]].filter((i) => !!i) : '',
          cargoInfoPhoto:
            imgList.length === 2 ? [imgList[0]].filter((i) => !!i) : '',
          status:
            status > 3 || receiptPics ? i18n.t(LOCALS.goods_received) : '-',
          receivingPhoto: JSON.parse(receiptPics || '[]'),
          createTime: getTimeFormat(goodsReceivedTime),
          memberId,
          receiverName,
        };
        setLogisticsInfo(logisticsInfo);
        /** 退货信息*/
        const returnGoodsInfo = {
          returnExpressCompany,
          returnTrackingNumber,
          returnVoucher: JSON.parse(returnVoucher),
          cargoInfoPhoto: JSON.parse(shippingDocument),
          returnType: [
            i18n.t(LOCALS.normal_transaction),
            i18n.t(LOCALS.in_store_pickup),
            i18n.t(LOCALS.mail),
          ][returnType],
          time: getTimeFormat(returnTime),
        };
        setReturnGoods(returnGoodsInfo);
        /** 收款信息 */
        const price =
          type === 2 ? thousands(finalRecyclePrice) : thousands(finalSalePrice);
        const paymentInfoList = GetPaymentInfo({
          ...omsRecycleOrder,
        });
        const memberPaymentDetails = memberData?.paymentDetails?.trim() || '';

        const parseVoucherKeys = (raw: string): string[] => {
          if (!raw) return [];
          const trimmed = raw.trim();
          if (!trimmed) return [];
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              return parsed
                .map((item) => (typeof item === 'string' ? item.trim() : ''))
                .filter((item) => item.length > 0);
            }
            if (typeof parsed === 'string' && parsed.trim().length > 0) {
              return [parsed.trim()];
            }
          } catch {
            // raw is not JSON, fallback to original string
            return [trimmed];
          }
          return [];
        };

        const resolveVoucherUrls = async (raw: string): Promise<string[]> => {
          const keys = parseVoucherKeys(raw);
          if (keys.length === 0) return [];
          const urls = await Promise.all(
            keys.map(async (key) => {
              if (!key) return '';
              if (/^https?:\/\//i.test(key)) return key;
              try {
                const signed = await commonApi.getSignedFileUrl(key);
                return signed.url || key;
              } catch {
                return key;
              }
            }),
          );
          return urls.filter((url) => url && url.length > 0);
        };

        const updateCollectionInfo = async () => {
          const voucherUrls = await resolveVoucherUrls(financialPaymentVoucher);
          const hasVoucher = voucherUrls.length > 0;
          let collectionInfoStatus = '';
          if (status === OMS_RECYCLE_ORDER_STATUS_MAP.COMPLETE) {
            collectionInfoStatus = i18n.t(LOCALS.payment_completed);
          } else {
            collectionInfoStatus = hasVoucher
              ? i18n.t(LOCALS.payment_completed)
              : i18n.t(LOCALS.pending_payment_r);
          }
          setCollectionInfo({
            financialPaymentInfo: paymentInfoList,
            financialPaymentAmount: type ? `${currency} ${price}` : '-',
            financialPaymentVoucher: voucherUrls,
            settlementType: [
              '',
              i18n.t(LOCALS.cash),
              i18n.t(LOCALS.accounting_settlement),
            ][settlementType],
            time: getTimeFormat(financialPaymentTime),
            status: collectionInfoStatus,
            paymentDetails: memberPaymentDetails || undefined,
          });
        };

        updateCollectionInfo();
        /** 合同信息 */
        let contractStatus = '-';
        if ([1, 2, 3, 4, 5, 10, 11, 12, 13, 14].includes(status))
          contractStatus = '-';
        if ([6, 7, 8, 9].includes(status))
          contractStatus = i18n.t(LOCALS.in_effect);
        const contract = {
          time: getTimeFormat(contractExpirationTime),
          url: contractLinkWebsite,
          contractLinkShopAdmin,
          contractLinkShopMember,
          status: contractStatus,
          type,
        };
        setContractList([contract]);
      }
      /** 日志信息 */
      if (omsRecycleOrderLogList) {
        const logList: OmsRecycleOrderLogInfo[] = [];

        omsRecycleOrderLogList.forEach((d) => {
          const {
            createTime,
            memberRemark,
            createBy,
            shopRemark,
            memberId,
            id,
            beforeStatus,
            afterStatus,
            name,
            operationImages,
          } = d;
          // const createByName =
          //   staffSelectOptions.find((d) => d.value === createBy)?.label ||
          //   createBy;
          const personnel = createBy
            ? `${i18n.t(LOCALS.shop)}：${name}`
            : memberId
              ? i18n.t(LOCALS.user)
              : i18n.t(LOCALS.system);
          const remark = memberRemark || shopRemark || '-';
          logList.push({
            time: getTimeFormat(createTime),
            personnel,
            remark,
            id,
            operationImages,
            statusChage: `${
              beforeStatus ? StatusMapping[beforeStatus] : '-'
            } -> ${afterStatus ? StatusMapping[afterStatus] : '-'}`,
          });
        });
        setLogList(logList);
      }
    }
  }, [
    dataSource,
    memberData,
    countryOptions,
    cityList,
    staffSelectOptions,
    LabelData,
    fullStamp,
  ]);

  /** 加载接口 */
  const getDetail = useCallback(() => {
    setLoading(true);
    fetchRecycleOrderDetail({ id })
      .then((data) => {
        setDataSource(data.data);
        /** 加载会员信息 */
        getMemberDetail(data.data?.omsRecycleOrder?.memberId || 0);
        /** 城市信息 */
        getCityList(data.data?.omsRecycleOrderLogistics?.country || '');
        //
        setProdInfo(data.data?.omsRecycleOrderProduct || {});
        setFullStamp(data.data.omsRecycleOrderProduct?.fullStamp || '');
        /** 加载AI鉴定详情 */
        const authenticationBagId =
          data.data?.omsRecycleOrder?.authenticationBagId;
        if (authenticationBagId) {
          authenticationApi
            .getAuthenticationDetail(String(authenticationBagId))
            .then((res) => {
              setAuthenticationDetail(res);
            })
            .catch(() => {
              setAuthenticationDetail(null);
            });
        } else {
          setAuthenticationDetail(null);
        }

        if (id) {
          fetchSignedContractInfo(id)
            .then((res) => setSignedContractInfo(res))
            .catch(() => setSignedContractInfo(null));
        } else {
          setSignedContractInfo(null);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, setProdInfo]);

  useEffect(() => {
    getDetail();
  }, [getDetail]);

  /** 获取会员 */
  const getMemberDetail = (memberId: number) => {
    getMemberById(memberId).then((data) => {
      const { data: memberData } = data;
      setMemberData(memberData);
    });
  };

  /** 城市信息 */
  const getCityList = (country: string) => {
    if (!country) return;
    getCityListByCountryCode(country).then((res) => {
      setCityList(res.data.cityList || []);
    });
  };

  // 判断是否是支持的包型 (BKC)
  const isSupportedBagType = useMemo(() => {
    const productCategoryId =
      dataSource?.omsRecycleOrderProduct?.productCategoryId;
    if (!productCategoryId) return false;

    const bagTypeName = getBagTypeNameFromCategoryId(
      productCategoryId,
      productCategoryCascaderOptions,
    );
    return checkIsSupportedBagType(bagTypeName);
  }, [
    dataSource?.omsRecycleOrderProduct?.productCategoryId,
    productCategoryCascaderOptions,
  ]);

  // 关联的产品ID
  const productId = dataSource?.omsRecycleOrderItem?.productId;

  return {
    loading,
    dataSource,
    currentStep,
    baseInfo,
    customerInfo,
    userProductInfo,
    productInfo,
    logisticsInfo,
    returnGoodsInfo,
    collectionInfo,
    logList,
    contractList,
    signedContractInfo,
    getDetail,
    RecycleOrderStatus: dataSource?.omsRecycleOrder?.status || 0,
    authenticationDetail,
    isSupportedBagType,
    productId,
  };
};

export default useHandleDetailData;
